import { useState } from 'react';
import { Modal, Box, Button, Checkbox, FormControlLabel, IconButton, Fade } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faPlug } from '@fortawesome/free-solid-svg-icons';
import { useTranslation, Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import '../translations.js';

const MetaIntegrationModal = ({ open, onClose, userId }) => {
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const saveModalPreference = async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}user_modal_preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          modal_name: 'meta_integration_modal'
        })
      });
    } catch (error) {
      console.error('Error saving modal preference:', error);
    }
  };

  const handleClose = async () => {
    if (doNotShowAgain && !submitting) {
      await saveModalPreference();
    }
    setDoNotShowAgain(false);
    onClose();
  };

  const handleDoNotShowAgainChange = async (checked) => {
    setDoNotShowAgain(checked);

    if (checked) {
      await saveModalPreference();
      setDoNotShowAgain(false);
      onClose();
    }
  };

  const handleConnectClick = async () => {
    if (submitting) return;

    setSubmitting(true);
    try {
      // Save preference to not show modal again
      await saveModalPreference();

      setDoNotShowAgain(false);
      onClose();

      // Navigate to Connect -> Messaging
      navigate('/social-media');
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Fade in={open} timeout={300}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'white',
            borderRadius: '10px',
            boxShadow: 24,
            p: isMobile ? 2.5 : 4,
            width: isMobile ? '85%' : '90%',
            maxWidth: 620,
            minHeight: 520,
            maxHeight: '80vh',
            overflow: 'auto',
            outline: 'none',
          }}
        >
          {/* CLOSE BUTTON */}
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'text.secondary',
              '&:hover': { color: 'text.primary' },
            }}
            aria-label={t('close')}
          >
            <FontAwesomeIcon icon={faXmark} />
          </IconButton>

          {/* BETA BADGE */}
          <span style={{
            position: 'absolute',
            top: 16,
            left: 16,
            background: '#f3eaff',
            color: '#5616ea',
            fontSize: '12px',
            fontWeight: 600,
            padding: '4px 10px',
            borderRadius: '12px',
            border: '1px solid #e0d4ff'
          }}>
            {t('meta_modal_beta_release')}
          </span>

          {/* META TECH PROVIDER BADGE */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <img
              src="/meta-tech-provider-logo.webp"
              alt={t('meta_modal_logo_alt')}
              style={{ maxWidth: '280px', height: 'auto' }}
            />
          </Box>

          {/* TITLE */}
          <h2 style={{
            margin: 0,
            fontSize: isMobile ? '22px' : '26px',
            fontWeight: 'bold',
            textAlign: 'center',
            marginBottom: '8px'
          }}>
            {t('meta_modal_title')}
          </h2>

          {/* PLATFORM LOGOS */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 3,
            mb: 3
          }}>
            <img
              src="/facebook-logo.webp"
              alt="Facebook"
              style={{ width: '60px', height: '60px' }}
            />
            <img
              src="/instagram-logo.webp"
              alt="Instagram"
              style={{ width: '60px', height: '60px' }}
            />
          </Box>

          {/* DESCRIPTION */}
          <Box sx={{ mb: 3 }}>
            <p style={{
              margin: 0,
              fontSize: '15px',
              lineHeight: 1.7,
              color: '#444',
              textAlign: 'center'
            }}>
              <Trans
                i18nKey="meta_modal_description_html"
                components={{ s: <strong style={{ color: '#5616ea' }} /> }}
              />
            </p>
          </Box>

          {/* BENEFITS LIST */}
          <Box sx={{ pl: 1, mb: 2 }}>
            <ul style={{
              margin: 0,
              paddingLeft: '20px',
              lineHeight: 1.8,
              color: '#444',
              fontSize: '14px'
            }}>
              <li><Trans i18nKey="meta_modal_benefit_responses_html" components={{ s: <strong /> }} /></li>
              <li><Trans i18nKey="meta_modal_benefit_nocode_html" components={{ s: <strong /> }} /></li>
              <li><Trans i18nKey="meta_modal_benefit_secure_html" components={{ s: <strong /> }} /></li>
              <li><Trans i18nKey="meta_modal_benefit_sameai_html" components={{ s: <strong /> }} /></li>
            </ul>
          </Box>

          {/* BETA NOTE */}
          <Box sx={{
            bgcolor: '#fafafa',
            borderRadius: '8px',
            p: 1.5,
            mb: 2,
            border: '1px solid #eee'
          }}>
            <p style={{
              margin: 0,
              fontSize: '13px',
              color: '#666',
              textAlign: 'center'
            }}>
              {t('meta_modal_beta_note')}
            </p>
          </Box>

          {/* DO NOT SHOW AGAIN */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={doNotShowAgain}
                  onChange={(e) => handleDoNotShowAgainChange(e.target.checked)}
                  sx={{
                    color: '#5616ea',
                    '&.Mui-checked': {
                      color: '#5616ea',
                    },
                  }}
                />
              }
              label={t('do_not_show_again')}
              sx={{ mb: 2 }}
            />
          </Box>

          {/* BUTTONS */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 2 }}>
            <Button
              onClick={handleClose}
              variant="outlined"
              disabled={submitting}
              sx={{
                color: '#5616ea',
                borderColor: '#5616ea',
                '&:hover': { borderColor: '#5e0fb3', backgroundColor: '#f3eaff' },
              }}
            >
              {t('meta_modal_maybe_later')}
            </Button>

            <Button
              onClick={handleConnectClick}
              variant="contained"
              disabled={submitting}
              startIcon={<FontAwesomeIcon icon={faPlug} />}
              sx={{
                backgroundColor: '#5616ea',
                '&:hover': { backgroundColor: '#4a14d6' },
              }}
            >
              {submitting ? t('connecting_ellipsis') : t('meta_modal_connect_platforms')}
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default MetaIntegrationModal;
